import React, { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { authService } from '../../services/authService';
import { graphService } from '../../services/graphService';
import { clearMsalInteractionState, clearMsalCache } from '../../utils/authRecovery';
import './AuthWrapper.css';

interface AuthWrapperProps {
  children: React.ReactNode;
}

/**
 * How long an interaction may sit unfinished before we offer an escape hatch.
 * A redirect round-trip that has not resolved by now is not going to.
 */
const STALLED_AFTER_MS = 15000;

const BUSY_MESSAGES: Partial<Record<InteractionStatus, string>> = {
  [InteractionStatus.Startup]: 'Starting up...',
  [InteractionStatus.HandleRedirect]: 'Completing sign-in...',
  [InteractionStatus.Login]: 'Redirecting to Microsoft...',
  [InteractionStatus.Logout]: 'Signing out...',
};

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();

  // `inProgress` is MSAL's own state machine — Startup while it initializes,
  // HandleRedirect while it processes a returning auth response, Login during
  // loginRedirect. Deriving "busy" from it (instead of the local isLoading
  // flag this component used to keep) is what stops the sign-in card being
  // rendered underneath an in-flight redirect, where clicking it threw
  // `interaction_in_progress` into a swallowed console.error.
  const busy = inProgress !== InteractionStatus.None;

  const [stalled, setStalled] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  // interaction_in_progress is recoverable, so it gets its own affordance
  // rather than being shown as a dead-end error.
  const [loginBlocked, setLoginBlocked] = useState(false);

  useEffect(() => {
    if (!busy) {
      setStalled(false);
      return;
    }
    const timer = setTimeout(() => setStalled(true), STALLED_AFTER_MS);
    return () => clearTimeout(timer);
  }, [busy, inProgress]);

  useEffect(() => {
    if (!isAuthenticated || accounts.length === 0) return;
    try {
      const msalInstance = authService.getInstance();
      if (!msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(accounts[0]);
      }
      graphService.setMsalInstance(msalInstance);
    } catch (error) {
      console.error('Auth initialization error:', error);
    }
  }, [isAuthenticated, accounts]);

  const handleLogin = async () => {
    setLoginError(null);
    setLoginBlocked(false);
    try {
      await authService.loginRedirect();
    } catch (error) {
      // Surfaced rather than only logged: a silent failure here is
      // indistinguishable from a dead button.
      console.error('Login error:', error);

      const code = (error as { errorCode?: string })?.errorCode ?? '';
      const blocked =
        code === 'interaction_in_progress' || String(error).includes('interaction_in_progress');

      if (blocked) {
        // Leftover interaction state from an attempt that never finished.
        // Don't show MSAL's raw developer copy — say what happened and give
        // the user the one action that fixes it.
        setLoginBlocked(true);
        setLoginError('A previous sign-in attempt did not finish.');
      } else {
        setLoginError(
          error instanceof Error && error.message
            ? error.message
            : 'Sign-in could not be started. Please try again.',
        );
      }
    }
  };

  const reloadClean = () => {
    window.location.replace(window.location.origin + window.location.pathname);
  };

  // Recovers the common case: a redirect that never completed, leaving
  // `interaction.status` set. Keeps the user signed in.
  const handleRetry = () => {
    clearMsalInteractionState();
    reloadClean();
  };

  // Recovers the other case, which the retry above cannot touch: a corrupt
  // entry in MSAL's localStorage cache makes `initialize()` throw, MsalProvider
  // swallows the error, and `inProgress` never leaves 'startup'. Only clearing
  // the account/token cache fixes it, which signs the user out — hence the
  // separate, explicitly-labelled control rather than doing it silently.
  const handleResetSignIn = () => {
    clearMsalCache();
    reloadClean();
  };

  if (busy) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>{BUSY_MESSAGES[inProgress] ?? 'Signing in...'}</p>

        {stalled && (
          <div className="auth-stalled">
            <p>This is taking longer than expected.</p>
            <button type="button" className="auth-retry-button" onClick={handleRetry}>
              Retry sign-in
            </button>
            <p className="auth-stalled-alt">
              If retrying does not help, this clears the saved sign-in on this device and
              starts over. You will be asked to sign in again.
            </p>
            <button type="button" className="auth-reset-button" onClick={handleResetSignIn}>
              Reset sign-in and start over
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>R&R Realty AI</h1>
            <p>Sign in to access your AI assistant</p>
          </div>

          <div className="auth-content">
            <button
              type="button"
              className="login-button"
              onClick={handleLogin}
            >
              Sign in with Microsoft
            </button>

            {loginError && (
              <div className="auth-error" role="alert">
                <p>{loginError}</p>
                {loginBlocked && (
                  <button type="button" className="auth-error-action" onClick={handleRetry}>
                    Clear it and try again
                  </button>
                )}
              </div>
            )}

            <div className="auth-info">
              <p>Use your organizational account to sign in</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="authenticated-app">
      {children}
    </div>
  );
};

export default AuthWrapper;
